import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { theme, card } from "../../theme";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { useConsultantShell } from "./ConsultantShellContext";
import { errText, PIPELINE_ORDER, PIPELINE_STYLE, type PipelineRow, type RenewalRow } from "./workspace";

// ── Certification pipeline + renewals ─────────────────────────────────────
// Built from real engagement data (backend lib/certificationPipeline.ts): which
// standards are still being prepared, engaged with a body, booked, certified, or
// due for surveillance / renewal — and every milestone in the next 90 days.

const STAGE_LABELS: Record<string, string> = {
  preparing: "Preparing", engaged: "Body engaged", audit_booked: "Audit booked", certified: "Certified",
  surveillance_due: "Surveillance due", renewal_due: "Renewal due", overdue: "Overdue",
};

const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function PipelinePanel({ onOpen, showTable = false }: { onOpen: (orgId: string) => void; showTable?: boolean }) {
  const { scope } = useConsultantShell();
  const [data, setData] = useState<{ pipeline: PipelineRow[]; renewals: RenewalRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ pipeline: PipelineRow[]; renewals: RenewalRow[] }>("/consultant/workspace/pipeline", { scope })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(errText(e, "Could not load the certification pipeline")); });
    return () => { cancelled = true; };
  }, [scope]);

  if (error) return <div style={{ fontSize: 12.5, color: theme.color.dangerText, marginBottom: 16 }}>{error}</div>;
  if (!data) return null;

  const counts = Object.fromEntries(PIPELINE_ORDER.map((s) => [s, data.pipeline.filter((r) => r.stage === s).length]));
  const total = data.pipeline.length;
  const rows = data.pipeline
    .filter((r) => !stage || r.stage === stage)
    .sort((a, b) => (a.nextDate ?? "9999").localeCompare(b.nextDate ?? "9999") || a.organizationName.localeCompare(b.organizationName));

  const heading = { fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 10 };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 24 }}>
      <div style={{ ...card }}>
        <div style={heading}>Certification pipeline</div>
        {total === 0 ? (
          <div style={{ fontSize: 13, color: theme.color.subtle }}>No certifiable standards enrolled yet.</div>
        ) : (
          <>
            <div style={{ display: "flex", height: 10, borderRadius: 3, overflow: "hidden", gap: 1, marginBottom: 12 }} aria-hidden="true">
              {PIPELINE_ORDER.filter((s) => counts[s] > 0).map((s) => (
                <div key={s} style={{ flex: counts[s], background: PIPELINE_STYLE[s].color, opacity: 0.8 }} />
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {PIPELINE_ORDER.map((s) => (
                <button key={s} onClick={() => setStage((cur) => (cur === s ? "" : s))} aria-pressed={stage === s}
                  style={{ fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontFamily: theme.font,
                    border: `1px solid ${stage === s ? PIPELINE_STYLE[s].color : theme.color.border}`, background: PIPELINE_STYLE[s].bg, color: PIPELINE_STYLE[s].color }}>
                  {STAGE_LABELS[s]} {counts[s]}
                </button>
              ))}
            </div>
            {(showTable || stage) && (
              <div style={{ marginTop: 12, maxHeight: 320, overflowY: "auto" }}>
                {rows.map((r) => (
                  <button key={`${r.organizationId}:${r.frameworkId}`} onClick={() => onOpen(r.organizationId)}
                    style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", textAlign: "left", background: "none", border: "none", borderTop: `1px solid ${theme.color.border}`, padding: "7px 0", cursor: "pointer", fontFamily: theme.font }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>{r.organizationName}</span>
                      <span style={{ display: "block", fontSize: 11.5, color: theme.color.subtle }}>
                        {frameworkLabel(r.frameworkId)}{r.bureauName ? ` · ${r.bureauName}` : ""}{r.nextDate ? ` · ${r.nextLabel} ${fmt(r.nextDate)}` : ""}
                      </span>
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: PIPELINE_STYLE[r.stage]?.bg, color: PIPELINE_STYLE[r.stage]?.color, whiteSpace: "nowrap" }}>{r.stageLabel}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ ...card }}>
        <div style={heading}>Renewals and audits — next 90 days</div>
        {data.renewals.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.color.subtle }}>Nothing due in the next 90 days.</div>
        ) : (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {data.renewals.map((r, i) => {
              const days = Math.ceil((new Date(r.date).getTime() - Date.now()) / 86_400_000);
              return (
                <button key={`${r.organizationId}:${r.title}:${i}`} onClick={() => onOpen(r.organizationId)}
                  style={{ display: "flex", width: "100%", gap: 10, alignItems: "center", textAlign: "left", background: "none", border: "none", borderTop: i ? `1px solid ${theme.color.border}` : "none", padding: "8px 0", cursor: "pointer", fontFamily: theme.font }}>
                  <span style={{ width: 54, flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: days < 0 ? theme.color.dangerText : days <= 30 ? theme.color.warningText : theme.color.subtle }}>
                    {days < 0 ? `${-days}d late` : days === 0 ? "Today" : `${days}d`}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>{r.organizationName}</span>
                    <span style={{ display: "block", fontSize: 11.5, color: theme.color.subtle }}>{r.title}{r.frameworkId ? ` — ${frameworkLabel(r.frameworkId)}` : ""} · {fmt(r.date)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
